/**
 * The `/saved` filter sidebar (SPEC `saved-posts` § Sidebar sections).
 *
 * Structurally the feed's sidebar: a sticky island of accordion sections over a pinned footer, the
 * same `SidebarSection` chrome, the same value+onChange contract, and the feed's own sections
 * mounted unchanged — Groups and Profiles included. What is new is Lists, Tags and Saved date,
 * which take the top: the collection's own questions before "whose" and "which posts".
 *
 * The Profiles list here is **not** filtered to active profiles the way `/feed`'s is: archiving
 * hides a profile from the feed but never from the collection (§6.6), so a save by an archived
 * author has to stay reachable by its author's name.
 */

import type {
  ContentType,
  GroupDTO,
  PostType,
  ProfileRowDTO,
  SavedFiltersState,
  SavedListDTO,
  SavedTagDTO,
} from '@lw/shared';
import { cx } from '../../components';
import {
  FeedAttributesSection,
  FeedContentTypesSection,
  FeedGroupsSection,
  FeedLengthSection,
  FeedPerformanceSection,
  FeedPostTypesSection,
  FeedProfilesSection,
} from '../feed/FeedSections';
import { SidebarSection } from '../feed/SidebarSection';
import { SavedDateSection, SavedListsSection, SavedTagsSection } from './SavedSections';
import {
  SAVED_SECTIONS,
  SAVED_SECTION_LABEL,
  clearSection,
  sectionCount,
  totalFilterCount,
  type SavedSectionId,
} from './savedState';

export type SavedSidebarProps = {
  filters: SavedFiltersState;
  onChange: (next: SavedFiltersState) => void;
  lists: readonly SavedListDTO[];
  tags: readonly SavedTagDTO[];
  groups: readonly GroupDTO[];
  /** Every profile, archived included — a collection outlives archiving. */
  profiles: readonly ProfileRowDTO[];
  mediaEnabled: boolean;
  countedPostTypes: readonly PostType[];
  openSections: ReadonlySet<SavedSectionId>;
  onToggleSection: (section: SavedSectionId) => void;
  className?: string;
};

export function SavedSidebar({
  filters,
  onChange,
  lists,
  tags,
  groups,
  profiles,
  mediaEnabled,
  countedPostTypes,
  openSections,
  onToggleSection,
  className,
}: SavedSidebarProps) {
  const listPatch = <
    K extends 'listIds' | 'tagIds' | 'groupIds' | 'profileIds' | 'postTypes' | 'contentTypes',
  >(
    key: K,
    next: NonNullable<SavedFiltersState[K]>,
  ) => {
    const updated: SavedFiltersState = { ...filters };
    if (next.length === 0) delete updated[key];
    else updated[key] = next;
    onChange(updated);
  };

  const total = totalFilterCount(filters);

  const body = (section: SavedSectionId) => {
    switch (section) {
      case 'lists':
        return (
          <SavedListsSection
            lists={lists}
            selected={filters.listIds ?? []}
            onChange={(next) => listPatch('listIds', next)}
          />
        );
      case 'tags':
        return (
          <SavedTagsSection
            tags={tags}
            selected={filters.tagIds ?? []}
            onChange={(next) => listPatch('tagIds', next)}
          />
        );
      case 'savedDate':
        return (
          <SavedDateSection
            value={{
              ...(filters.savedFrom === undefined ? {} : { from: filters.savedFrom }),
              ...(filters.savedTo === undefined ? {} : { to: filters.savedTo }),
            }}
            onChange={(next) => {
              const updated: SavedFiltersState = { ...filters };
              if (next.from === undefined) delete updated.savedFrom;
              else updated.savedFrom = next.from;
              if (next.to === undefined) delete updated.savedTo;
              else updated.savedTo = next.to;
              onChange(updated);
            }}
          />
        );
      case 'groups':
        return (
          <FeedGroupsSection
            groups={groups}
            selected={filters.groupIds ?? []}
            onChange={(next) => listPatch('groupIds', next)}
          />
        );
      case 'profiles':
        return (
          <FeedProfilesSection
            profiles={profiles}
            selected={filters.profileIds ?? []}
            onChange={(next) => listPatch('profileIds', next)}
            mediaEnabled={mediaEnabled}
          />
        );
      case 'postTypes':
        return (
          <FeedPostTypesSection
            selected={filters.postTypes ?? []}
            onChange={(next: PostType[]) => listPatch('postTypes', next)}
            countedPostTypes={countedPostTypes}
          />
        );
      case 'contentTypes':
        return (
          <FeedContentTypesSection
            selected={filters.contentTypes ?? []}
            onChange={(next: ContentType[]) => listPatch('contentTypes', next)}
          />
        );
      case 'performance':
        return (
          <FeedPerformanceSection
            value={filters.perf ?? []}
            onChange={(next) => {
              const updated: SavedFiltersState = { ...filters };
              if (next.length === 0) delete updated.perf;
              else updated.perf = next;
              onChange(updated);
            }}
          />
        );
      case 'attributes':
        return (
          <FeedAttributesSection
            value={{
              ...(filters.hasMedia === undefined ? {} : { hasMedia: filters.hasMedia }),
              ...(filters.edited === undefined ? {} : { edited: filters.edited }),
              ...(filters.hasLink === undefined ? {} : { hasLink: filters.hasLink }),
            }}
            onChange={(next) => {
              const updated: SavedFiltersState = { ...filters };
              for (const key of ['hasMedia', 'edited', 'hasLink'] as const) {
                if (next[key] === undefined) delete updated[key];
                else updated[key] = next[key];
              }
              onChange(updated);
            }}
          />
        );
      case 'length':
        return (
          <FeedLengthSection
            value={{
              ...(filters.lenUnit === undefined ? {} : { unit: filters.lenUnit }),
              ...(filters.lenMin === undefined ? {} : { min: filters.lenMin }),
              ...(filters.lenMax === undefined ? {} : { max: filters.lenMax }),
            }}
            onChange={(next) => {
              const updated: SavedFiltersState = { ...filters };
              if (next.unit === undefined) {
                delete updated.lenUnit;
                delete updated.lenMin;
                delete updated.lenMax;
              } else {
                updated.lenUnit = next.unit;
                if (next.min === undefined) delete updated.lenMin;
                else updated.lenMin = next.min;
                if (next.max === undefined) delete updated.lenMax;
                else updated.lenMax = next.max;
              }
              onChange(updated);
            }}
          />
        );
    }
  };

  return (
    <div className={cx('feed-sidebar', className)}>
      <div className="feed-sidebar__scroll">
        {SAVED_SECTIONS.map((section) => (
          <SidebarSection
            key={section}
            title={SAVED_SECTION_LABEL[section]}
            open={openSections.has(section)}
            onToggle={() => onToggleSection(section)}
            count={sectionCount(filters, section)}
            onClear={() => onChange(clearSection(filters, section))}
          >
            {body(section)}
          </SidebarSection>
        ))}
      </div>

      {/* Date range and sort are header state and are deliberately not counted here. */}
      <footer className="feed-sidebar__foot">
        {total === 0 ? (
          <span className="muted">No filters applied</span>
        ) : (
          <button type="button" className="btn-ghost btn-ghost--sm" onClick={() => onChange({})}>
            Clear {total} {total === 1 ? 'filter' : 'filters'}
          </button>
        )}
      </footer>
    </div>
  );
}

export default SavedSidebar;
